import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { AxiosInstance } from 'axios'
import {
  AssignedMembersData,
  ChannelData,
  ConversionRow,
  FollowupPerformanceResponse,
  ForecastVsActualData,
  SalespersonOption,
  TotalSalesPerMonthSeries,
} from './model'
import { satangToBaht } from '@/utils/helper/stang-to-baht.helper'

const base = '/postgrest'
const POSTGREST_PAGE_SIZE = 500

const toNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNullableNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const toNullableString = (value: unknown) => (typeof value === 'string' ? value : null)
const toNullableNonEmptyString = (value: unknown) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const toIsoStart = (date: string) => `${date}T00:00:00.000Z`
const isLaterDate = (next: string, current: string | null) => {
  if (!current) return true
  const nextTs = Date.parse(next)
  const currentTs = Date.parse(current)

  if (!Number.isNaN(nextTs) && !Number.isNaN(currentTs)) {
    return nextTs > currentTs
  }

  return next > current
}

const toTotal = (pagination: Record<string, unknown> | undefined, fallback: number) => {
  const total = Number(pagination?.total)
  return Number.isFinite(total) ? total : fallback
}

const quoteList = (values: string[]) =>
  values.map((value) => `"${value.replace(/"/g, '\\"')}"`).join(',')

const chunk = <T>(values: T[], size: number) => {
  const chunks: T[][] = []
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size))
  }
  return chunks
}

// QTY-2097: one name per status, the same the CRM tables use — MEMBER is a
// member (not a "Closed Won"), UNQUALIFIED is unqualified (not "Unclassified").
const formatStatus = (status: string) => {
  if (status === 'INCOMPLETED') return 'Incomplete'
  if (status === 'LEAD') return 'Lead'
  if (status === 'PROSPECT') return 'Prospect'
  if (status === 'OPEN_OPPORTUNITY') return 'Open Opportunity'
  if (status === 'CLOSE_WON') return 'Close Won'
  if (status === 'CLOSE_LOST') return 'Close Lost'
  if (status === 'UNQUALIFIED') return 'Unqualified'
  if (status === 'MEMBER') return 'Member'
  return status
}

const toPackageCategory = (salesCategory: string | null, packageTypeName: string | null) => {
  if (salesCategory === 'MEMBERSHIP') return 'membership'
  if (salesCategory === 'PERSONAL_TRAINER') return 'personalTrainer'
  if (salesCategory === 'SWIMMING') return 'swimming'

  const normalizedPackageType = packageTypeName?.toLowerCase() ?? ''
  if (normalizedPackageType.includes('facility') || normalizedPackageType.includes('wellness')) {
    return 'facility'
  }
  if (normalizedPackageType.includes('group') || normalizedPackageType.includes('class')) {
    return 'groupClass'
  }

  return null
}

export const IndividualSaleSummaryService = (client: AxiosInstance) => {
  const buildFetchAllQuery = ({
    select,
    page,
    order,
    distinct,
    filters,
    withCount,
  }: {
    select: string
    page: number
    order?: string
    distinct?: string
    filters?: string[]
    withCount: boolean
  }) => {
    const query = new URLSearchParams()
    query.append('select', select)
    if (withCount) {
      query.append('count', 'true')
    }
    query.append('page', String(page))
    query.append('pageSize', String(POSTGREST_PAGE_SIZE))
    if (order) {
      query.append('order', order)
    }
    if (distinct) {
      query.append('distinct', distinct)
    }
    ;(filters ?? []).forEach((filter) => query.append('filter', filter))
    return query
  }

  const fetchAllRows = async <T>({
    resource,
    select,
    order,
    distinct,
    filters,
  }: {
    resource: string
    select: string
    order?: string
    distinct?: string
    filters?: string[]
  }): Promise<T[]> => {
    const fetchPage = (page: number, withCount: boolean) =>
      wrapApiCall(() =>
        client.get<{ data?: T[]; pagination?: Record<string, unknown> }>(
          `${base}/${resource}?${buildFetchAllQuery({
            select,
            page,
            order,
            distinct,
            filters,
            withCount,
          }).toString()}`
        )
      )

    // Page 1 requests the count so we know the total up front; every other
    // page skips `count=true` (the backend would otherwise re-run the full
    // view just to recompute an unchanging total) and all of them are fetched
    // in parallel instead of sequentially, since the total is already known.
    const { data: firstData } = await fetchPage(1, true)
    const firstRows = firstData?.data ?? []

    const total = toTotal(firstData?.pagination, firstRows.length)
    const totalPages = clampTotalPages(
      total,
      POSTGREST_PAGE_SIZE,
      'individual-sale-summary.fetchAllRows'
    )

    if (totalPages <= 1) {
      return firstRows
    }

    const restPages = await fetchPagesInBatches(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
      (page) => fetchPage(page, false).then(({ data }) => data?.data ?? [])
    )

    return [firstRows, ...restPages].flat()
  }

  const fetchRowsByFilterChunks = async <T>({
    resource,
    select,
    filterColumn,
    values,
    valueType,
    filters,
  }: {
    resource: string
    select: string
    filterColumn: string
    values: Array<number | string>
    valueType: 'number' | 'string'
    filters?: string[]
  }): Promise<T[]> => {
    if (!values.length) return []

    const rows = await Promise.all(
      chunk(values, 100).map((items) =>
        fetchAllRows<T>({
          resource,
          select,
          filters: [
            ...(filters ?? []),
            `${filterColumn}=in.(${
              valueType === 'string' ? quoteList(items.map(String)) : items.join(',')
            })`,
          ],
        })
      )
    )

    return rows.flat()
  }

  // Ids of everyone holding a sales-owner role, or the Sale add-on preset on
  // top of another role (`includePresets`) — the same roster the lead and
  // Sales List pickers offer, so a preset holder isn't missing from the
  // manager's dropdown.
  const fetchSalesOwnerIds = async (): Promise<Set<number>> => {
    const { data: roleCategories } = await wrapApiCall(() =>
      client.get<Record<string, number[]>>('/roles/categories')
    )

    const saleRoleIds = (roleCategories?.['sales-owner'] ?? []).filter(
      (id): id is number => typeof id === 'number'
    )

    const allowedSalesIds = new Set<number>()
    if (!saleRoleIds.length) return allowedSalesIds

    const fetchStaffPage = (page: number) =>
      wrapApiCall(() =>
        client.get<{
          data?: Record<string, unknown>[]
          pagination?: { total?: number }
        }>('/users/staff', {
          params: {
            page,
            pageSize: POSTGREST_PAGE_SIZE,
            roleIds: saleRoleIds.join(','),
            includePresets: true,
          },
        })
      )

    // `/users/staff` always returns `pagination.total`, so page 1 tells us
    // the total up front and the remaining pages can be fetched in
    // parallel instead of one-at-a-time.
    const { data: firstData } = await fetchStaffPage(1)
    const firstStaffRows = firstData?.data ?? []
    const total = Number(firstData?.pagination?.total ?? firstStaffRows.length)
    const totalPages = clampTotalPages(
      total,
      POSTGREST_PAGE_SIZE,
      'individual-sale-summary.getSalespersons.staff'
    )

    const restStaffRows =
      totalPages > 1
        ? await fetchPagesInBatches(
            Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
            (page) => fetchStaffPage(page).then(({ data }) => data?.data ?? [])
          )
        : []

    ;[firstStaffRows, ...restStaffRows].flat().forEach((staff) => {
      const id = toNullableNumber(staff.id)
      if (id) {
        allowedSalesIds.add(id)
      }
    })

    return allowedSalesIds
  }

  return {
    /**
     * `ownerId` scopes the list to that one salesperson (QTY-2070: a
     * staff-level sale). The backend refuses such a caller any operator but
     * `eq`/`in`-self on an owner column, so the team-wide `is.not_null` filter
     * is a 403 for them. They are also already known to be a salesperson,
     * which is why the roster lookup is skipped — a preset holder's own role
     * may not be a sales-owner one.
     */
    async getSalespersons(year: number, ownerId?: number): Promise<SalespersonOption[]> {
      const yearFrom = `${year}-01-01`
      const yearTo = `${year + 1}-01-01`
      const isOwnScope = ownerId !== undefined

      const [allowedSalesIds, salesFactRows, opportunityRows] = await Promise.all([
        isOwnScope ? Promise.resolve(new Set([ownerId])) : fetchSalesOwnerIds(),
        fetchAllRows<Record<string, unknown>>({
          resource: 'sales_fact_view',
          select: 'seller_user_id,seller_full_name',
          distinct: 'seller_user_id,seller_full_name',
          filters: [
            isOwnScope ? `seller_user_id=eq.${ownerId}` : 'seller_user_id=is.not_null',
            `sold_date_local=gte.${yearFrom}`,
            `sold_date_local=lt.${yearTo}`,
          ],
        }),
        fetchAllRows<Record<string, unknown>>({
          resource: 'dashboard_opportunity_fact_view',
          select: 'sales_user_id,sales_full_name',
          distinct: 'sales_user_id,sales_full_name',
          filters: [
            isOwnScope ? `sales_user_id=eq.${ownerId}` : 'sales_user_id=is.not_null',
            `year_local=eq.${year}`,
          ],
        }),
      ])

      const byId = new Map<number, string>()

      salesFactRows.forEach((row) => {
        const id = toNullableNumber(row.seller_user_id)
        const name = toNullableString(row.seller_full_name)
        if (!id || !name) return
        byId.set(id, name)
      })

      opportunityRows.forEach((row) => {
        const id = toNullableNumber(row.sales_user_id)
        const name = toNullableString(row.sales_full_name)
        if (!id || !name) return
        if (!byId.has(id)) {
          byId.set(id, name)
        }
      })

      return Array.from(byId.entries())
        .filter(([id]) => allowedSalesIds.has(id))
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    async getTotalSalesPerMonth(
      year: number,
      salesUserId?: number
    ): Promise<TotalSalesPerMonthSeries> {
      const yearFrom = `${year}-01-01`
      const yearTo = `${year + 1}-01-01`
      const filters = [`sold_date_local=gte.${yearFrom}`, `sold_date_local=lt.${yearTo}`]

      if (salesUserId) {
        filters.push(`seller_user_id=eq.${salesUserId}`)
      }

      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'sales_fact_view',
        select: 'sold_date_local,sales_category,package_type_name_en,line_total_thb',
        filters,
      })

      const membership = Array.from({ length: 12 }, () => 0)
      const personalTrainer = Array.from({ length: 12 }, () => 0)
      const swimming = Array.from({ length: 12 }, () => 0)
      const facility = Array.from({ length: 12 }, () => 0)
      const groupClass = Array.from({ length: 12 }, () => 0)

      rows.forEach((row) => {
        const soldDate = toNullableString(row.sold_date_local)
        if (!soldDate) return

        const monthIndex = new Date(soldDate).getUTCMonth()
        if (monthIndex < 0 || monthIndex > 11) return

        const value = satangToBaht(toNumber(row.line_total_thb))
        const category = toPackageCategory(
          toNullableString(row.sales_category),
          toNullableString(row.package_type_name_en)
        )

        if (category === 'membership') {
          membership[monthIndex] += value
        }
        if (category === 'personalTrainer') {
          personalTrainer[monthIndex] += value
        }
        if (category === 'swimming') {
          swimming[monthIndex] += value
        }
        if (category === 'facility') {
          facility[monthIndex] += value
        }
        if (category === 'groupClass') {
          groupClass[monthIndex] += value
        }
      })

      return {
        membership,
        personalTrainer,
        swimming,
        facility,
        groupClass,
      }
    },

    async getLeadConversion(
      fromDate: string,
      toDate: string,
      salesUserId?: number
    ): Promise<ConversionRow[]> {
      const filters = [
        'entity_type=eq.LEAD',
        'customer_type=eq.NEW_CUSTOMER',
        `reference_at=gte.${toIsoStart(fromDate)}`,
        `reference_at=lt.${toIsoStart(toDate)}`,
      ]

      if (salesUserId) {
        filters.push(`sales_user_id=eq.${salesUserId}`)
      }

      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'dashboard_opportunity_fact_view',
        select: 'lead_id,status_raw,expected_closing_value_thb',
        filters,
      })

      const byLeadId = new Map<number, { status: string; value: number }>()
      rows.forEach((row) => {
        const leadId = toNullableNumber(row.lead_id)
        const status = toNullableString(row.status_raw)
        if (!leadId || !status) return

        const current = byLeadId.get(leadId) ?? { status, value: 0 }
        current.value += toNumber(row.expected_closing_value_thb)
        byLeadId.set(leadId, current)
      })

      const byStatus = new Map<string, { qty: number; value: number }>()

      byLeadId.forEach(({ status, value }) => {
        const current = byStatus.get(status) ?? { qty: 0, value: 0 }
        current.qty += 1
        current.value += value
        byStatus.set(status, current)
      })

      const lead = byStatus.get('LEAD') ?? { qty: 0, value: 0 }
      const prospect = byStatus.get('PROSPECT') ?? { qty: 0, value: 0 }
      const member = byStatus.get('MEMBER') ?? { qty: 0, value: 0 }
      const closeLost = byStatus.get('CLOSE_LOST') ?? { qty: 0, value: 0 }
      const unqualified = byStatus.get('UNQUALIFIED') ?? { qty: 0, value: 0 }

      const total = {
        qty: lead.qty + prospect.qty + member.qty + closeLost.qty + unqualified.qty,
        value: lead.value + prospect.value + member.value + closeLost.value + unqualified.value,
      }

      return [
        { status: 'Lead Generation', qty: total.qty, value: total.value },
        { status: 'Lead', qty: lead.qty, value: lead.value },
        {
          status: 'Prospect / Qualified lead',
          qty: prospect.qty,
          value: prospect.value,
        },
        { status: 'Member', qty: member.qty, value: member.value },
        { status: 'Close Lost', qty: closeLost.qty, value: closeLost.value },
        {
          status: 'Unqualified',
          qty: unqualified.qty,
          value: unqualified.value,
        },
      ]
    },

    async getRenewalConversion(
      fromDate: string,
      toDate: string,
      salesUserId?: number
    ): Promise<ConversionRow[]> {
      const filters = [
        'entity_type=eq.OPPORTUNITY',
        'customer_type=eq.EXISTING_CUSTOMER',
        `reference_at=gte.${toIsoStart(fromDate)}`,
        `reference_at=lt.${toIsoStart(toDate)}`,
      ]

      if (salesUserId) {
        filters.push(`sales_user_id=eq.${salesUserId}`)
      }

      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'dashboard_opportunity_fact_view',
        select: 'upsell_id,status_raw,expected_closing_value_thb',
        filters,
      })

      const byUpsellId = new Map<string, { status: string; value: number }>()
      rows.forEach((row) => {
        const upsellId = toNullableString(row.upsell_id)
        const status = toNullableString(row.status_raw)
        if (!upsellId || !status) return

        const current = byUpsellId.get(upsellId) ?? { status, value: 0 }
        current.value += toNumber(row.expected_closing_value_thb)
        byUpsellId.set(upsellId, current)
      })

      const byStatus = new Map<string, { qty: number; value: number }>()

      byUpsellId.forEach(({ status, value }) => {
        const current = byStatus.get(status) ?? { qty: 0, value: 0 }
        current.qty += 1
        current.value += value
        byStatus.set(status, current)
      })

      const open = byStatus.get('OPEN_OPPORTUNITY') ?? { qty: 0, value: 0 }
      const closeWon = byStatus.get('CLOSE_WON') ?? { qty: 0, value: 0 }
      const closeLost = byStatus.get('CLOSE_LOST') ?? { qty: 0, value: 0 }

      const total = {
        qty: open.qty + closeWon.qty + closeLost.qty,
        value: open.value + closeWon.value + closeLost.value,
      }

      return [
        {
          status: 'Renewal Opportunities',
          qty: total.qty,
          value: total.value,
        },
        {
          status: 'Open opportunity',
          qty: open.qty,
          value: open.value,
        },
        {
          status: 'Close Won',
          qty: closeWon.qty,
          value: closeWon.value,
        },
        {
          status: 'Close Lost',
          qty: closeLost.qty,
          value: closeLost.value,
        },
      ]
    },

    async getForecastVsActual(year: number, salesUserId?: number): Promise<ForecastVsActualData> {
      const filters = [`year_local=eq.${year}`]
      if (salesUserId) {
        filters.push(`sales_user_id=eq.${salesUserId}`)
      }

      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'dashboard_opportunity_fact_view',
        select: 'month_local,expected_closing_value_thb,closed_won_value_thb',
        filters,
      })

      const forecast = Array.from({ length: 12 }, () => 0)
      const actual = Array.from({ length: 12 }, () => 0)

      rows.forEach((row) => {
        const month = toNumber(row.month_local)
        const monthIndex = month - 1
        if (monthIndex < 0 || monthIndex > 11) return

        forecast[monthIndex] += toNumber(row.expected_closing_value_thb)
        actual[monthIndex] += toNumber(row.closed_won_value_thb)
      })

      const conversionRate = forecast.map((value, index) => {
        if (!value) return 0
        return (actual[index] / value) * 100
      })

      return { actual, forecast, conversionRate }
    },

    async getAssignedMembers(
      fromDate: string,
      toDate: string,
      salesUserId?: number
    ): Promise<AssignedMembersData> {
      const filters = [`month_start=gte.${fromDate}`, `month_start=lt.${toDate}`]

      if (salesUserId) {
        filters.push(`sale_owner_id=eq.${salesUserId}`)
      }

      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'member_monthly_latest_activities',
        select: 'member_type_code,assigned_member_status',
        filters,
      })

      const mapType = (memberTypeCode: string) => {
        if (memberTypeCode === 'MEMBER') return 0
        if (memberTypeCode === 'SESSION_MEMBER') return 1
        return 2
      }

      const categories = ['Member', 'Session Member', 'Drop-in']
      const active = [0, 0, 0]
      const inactive = [0, 0, 0]

      rows.forEach((row) => {
        const memberTypeCode = toNullableString(row.member_type_code)
        if (!memberTypeCode) return

        const index = mapType(memberTypeCode)
        const status = toNullableString(row.assigned_member_status)
        const isInactive = status === 'EXPIRED' || status === 'NOT_STARTED' || status === 'BANNED'

        if (isInactive) {
          inactive[index] += 1
          return
        }

        active[index] += 1
      })

      return { categories, active, inactive }
    },

    async getConversionChannels(
      fromDate: string,
      toDate: string,
      salesUserId?: number
    ): Promise<ChannelData> {
      const filters = [
        `month_start=gte.${fromDate}`,
        `month_start=lt.${toDate}`,
        `member_created_at=lt.${toDate}`,
      ]

      if (salesUserId) {
        filters.push(`sale_owner_id=eq.${salesUserId}`)
      }

      const rows = await fetchAllRows<Record<string, unknown>>({
        resource: 'member_monthly_latest_activities',
        select: 'referral_source',
        filters,
      })

      const grouped = new Map<string, number>()

      rows.forEach((row) => {
        const label = toNullableString(row.referral_source) ?? 'Unknown'
        grouped.set(label, (grouped.get(label) ?? 0) + 1)
      })

      const sorted = Array.from(grouped.entries()).sort((a, b) => b[1] - a[1])

      return {
        labels: sorted.map(([label]) => label),
        series: sorted.map(([, value]) => value),
      }
    },

    async getLeadFollowupPerformance(
      fromDate: string,
      toDate: string,
      page: number,
      pageSize: number,
      salesUserId?: number
    ): Promise<FollowupPerformanceResponse> {
      const leadFilters = ['status=in.(LEAD,PROSPECT)', `created_at=lt.${toIsoStart(toDate)}`]

      if (salesUserId) {
        leadFilters.push(`sales_user_id=eq.${salesUserId}`)
      }

      const leadRows = await fetchAllRows<Record<string, unknown>>({
        resource: 'leads_with_sales_view',
        select: 'id,code,contact_name,status',
        filters: leadFilters,
      })

      const leadById = new Map<number, { code: string; name: string; status: string }>()
      leadRows.forEach((row) => {
        const id = toNullableNumber(row.id)
        const code = toNullableString(row.code) ?? String(id)
        const name = toNullableString(row.contact_name) ?? '-'
        const status = toNullableString(row.status)
        if (!id || !status) return
        leadById.set(id, { code, name, status: formatStatus(status) })
      })

      const leadIds = Array.from(leadById.keys())

      const dueFollowupRows = await fetchRowsByFilterChunks<Record<string, unknown>>({
        resource: 'lead_folowups',
        select: 'lead_id,next_action_at,occured_at,result',
        filterColumn: 'lead_id',
        values: leadIds,
        valueType: 'number',
        filters: [
          `next_action_at=gte.${toIsoStart(fromDate)}`,
          `next_action_at=lt.${toIsoStart(toDate)}`,
        ],
      })

      const dueLeadIds = new Set<number>()
      dueFollowupRows.forEach((row) => {
        const leadId = toNullableNumber(row.lead_id)
        if (leadId && leadById.has(leadId)) {
          dueLeadIds.add(leadId)
        }
      })

      const allFollowupRows = await fetchRowsByFilterChunks<Record<string, unknown>>({
        resource: 'lead_folowups',
        select: 'lead_id,next_action_at,occured_at,result',
        filterColumn: 'lead_id',
        values: Array.from(dueLeadIds),
        valueType: 'number',
      })

      const stats = new Map<
        number,
        {
          qty: number
          latestCompletedDate: string | null
          latestResult: string | null
        }
      >()

      allFollowupRows.forEach((row) => {
        const leadId = toNullableNumber(row.lead_id)
        if (!leadId || !dueLeadIds.has(leadId)) return

        const occuredAt = toNullableString(row.occured_at)
        const result = toNullableNonEmptyString(row.result)
        const current = stats.get(leadId) ?? {
          qty: 0,
          latestCompletedDate: null,
          latestResult: null,
        }

        // QTY-2097: a booked appointment is not a follow-up done. Only rows
        // with a result count, dated by the day they actually happened.
        if (occuredAt) {
          current.qty += 1
          if (isLaterDate(occuredAt, current.latestCompletedDate)) {
            current.latestCompletedDate = occuredAt
            current.latestResult = result
          }
        }

        stats.set(leadId, current)
      })

      const rows = Array.from(dueLeadIds)
        .map((leadId) => {
          const stat = stats.get(leadId) ?? {
            qty: 0,
            latestCompletedDate: null,
            latestResult: null,
          }
          const lead = leadById.get(leadId)!

          return {
            id: lead.code,
            name: lead.name,
            status: lead.status,
            qty: stat.qty,
            date: stat.latestCompletedDate,
            result: stat.latestResult,
          }
        })
        .sort((a, b) => {
          if (b.qty !== a.qty) return b.qty - a.qty
          return (b.date ?? '').localeCompare(a.date ?? '')
        })

      const start = (page - 1) * pageSize

      return {
        data: rows.slice(start, start + pageSize),
        total: rows.length,
      }
    },

    async getRenewalOpportunityFollowupPerformance(
      fromDate: string,
      toDate: string,
      page: number,
      pageSize: number,
      salesUserId?: number
    ): Promise<FollowupPerformanceResponse> {
      type UpsellListItem = {
        id: string
        memberCode?: string
        name?: string
        status?: string
      }
      const upsellListResp = await wrapApiCall(() =>
        client.get<{ data?: UpsellListItem[] }>('/upsells', {
          params: {
            page: 1,
            pageSize: 1000,
            status: 'OPEN_OPPORTUNITY',
            ...(salesUserId ? { saleId: salesUserId } : {}),
          },
        })
      )

      const upsells: UpsellListItem[] = upsellListResp.data?.data ?? []

      type FollowUp = {
        nextActionAt: string | null
        occuredAt: string | null
        result: string | null
      }
      const followupsByUpsellId = new Map<string, FollowUp[]>()

      // Bulk-fetch follow-ups via postgrest (chunked upsell_id=in.(...)) —
      // mirrors the lead flow above. The previous per-upsell
      // GET /upsells/{id}/follow-ups fired one request per open upsell
      // (hundreds at once on this dashboard).
      const followupRows = await fetchRowsByFilterChunks<Record<string, unknown>>({
        resource: 'upsell_followups',
        select: 'upsell_id,next_action_at,occured_at,result',
        filterColumn: 'upsell_id',
        values: upsells.map((upsell) => upsell.id),
        valueType: 'string',
      })

      followupRows.forEach((row) => {
        const upsellId = toNullableString(row.upsell_id)
        if (!upsellId) return
        const list = followupsByUpsellId.get(upsellId) ?? []
        list.push({
          nextActionAt: toNullableString(row.next_action_at),
          occuredAt: toNullableString(row.occured_at),
          result: toNullableString(row.result),
        })
        followupsByUpsellId.set(upsellId, list)
      })

      const fromIso = toIsoStart(fromDate)
      const toIso = toIsoStart(toDate)

      const rows = upsells
        .map((upsell) => {
          const followups = followupsByUpsellId.get(upsell.id) ?? []
          const inMonth = followups.some((f) => {
            const at = f.nextActionAt
            return !!at && at >= fromIso && at < toIso
          })
          if (!inMonth) return null

          // QTY-2097: only recorded follow-ups count, dated by the day they
          // actually happened; a booked appointment is not work done.
          let qty = 0
          let latestCompletedDate: string | null = null
          let latestResult: string | null = null
          for (const f of followups) {
            if (!f.occuredAt) continue
            qty += 1
            if (isLaterDate(f.occuredAt, latestCompletedDate)) {
              latestCompletedDate = f.occuredAt
              latestResult = toNullableNonEmptyString(f.result)
            }
          }

          return {
            id: upsell.memberCode ?? '-',
            name: upsell.name ?? '-',
            status: formatStatus(upsell.status ?? 'OPEN_OPPORTUNITY'),
            qty,
            date: latestCompletedDate,
            result: latestResult,
          }
        })
        .filter((row): row is NonNullable<typeof row> => row !== null)
        .sort((a, b) => {
          if (b.qty !== a.qty) return b.qty - a.qty
          return (b.date ?? '').localeCompare(a.date ?? '')
        })

      const start = (page - 1) * pageSize

      return {
        data: rows.slice(start, start + pageSize),
        total: rows.length,
      }
    },
  }
}
